import { useRef, useState } from 'react';
import { FiCamera, FiImage, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { uploadPhoto, getEvidenceDisplayUrl } from '../lib/api';
import { compressImageForUpload } from '../lib/imageCompression';

/**
 * Reusable photo picker/uploader. Uploads to backend and calls onUploadComplete with the returned URL.
 * Offers "Take Photo" (camera) and "Choose from Gallery" (file picker) for mobile/tablet compatibility.
 * Props: currentUrl (string), onUploadComplete (callback(url)), label (optional).
 */
export function PhotoCapture({ currentUrl, onUploadComplete, label = 'Photo evidence' }) {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Some pickers report an empty type for HEIC; let the decode below be the judge
    // rather than rejecting a photo the user legitimately chose.
    if (file.type && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setIsUploading(true);
    e.target.value = '';
    try {
      const jpeg = await compressImageForUpload(file);
      const url = await uploadPhoto(jpeg);
      onUploadComplete(url);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Upload failed';
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    onUploadComplete(null);
  };

  return (
    <div className="form-group photo-capture-group">
      <label className="form-label-block">{label}</label>
      {currentUrl ? (
        <div className="photo-capture-preview">
          <img src={getEvidenceDisplayUrl(currentUrl)} alt="Evidence" className="photo-capture-thumb" />
          <button
            type="button"
            className="photo-capture-remove"
            onClick={handleRemove}
            title="Remove photo"
            aria-label="Remove photo"
          >
            <FiX size={18} />
          </button>
        </div>
      ) : (
        <div className="photo-capture-empty">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="photo-capture-input"
            onChange={handleFileSelect}
            disabled={isUploading}
            aria-hidden
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            className="photo-capture-input"
            onChange={handleFileSelect}
            disabled={isUploading}
            aria-hidden
          />
          <button
            type="button"
            className="btn btn-sm photo-capture-add"
            onClick={() => cameraInputRef.current?.click()}
            disabled={isUploading}
            aria-label="Take photo with camera"
          >
            {isUploading ? (
              <span className="photo-capture-spinner" aria-hidden />
            ) : (
              <FiCamera size={18} />
            )}
            <span>{isUploading ? 'Uploading…' : 'Take Photo'}</span>
          </button>
          <button
            type="button"
            className="btn btn-sm photo-capture-add"
            onClick={() => galleryInputRef.current?.click()}
            disabled={isUploading}
            aria-label="Choose from gallery"
          >
            <FiImage size={18} />
            <span>Choose from Gallery</span>
          </button>
        </div>
      )}
    </div>
  );
}
